import {
  MatrixTypeDto,
  TeamDevelopmentFilterDto,
  TeamMatrixSupervisorRelationDto,
  TeamPlanStatusDto,
} from '@/api/generated';
import { TeamTranslation } from '@/assets/locales/views/team/team.translation.en.ts';

export const teamTranslation: TeamTranslation = {
  actions: {
    approve: 'Zatwierdź',
    collapse: 'Zwiń',
    openProfile: 'Otwórz profil',
    preview: 'Podgląd',
    reject: 'Odrzuć',
    retry: 'Spróbuj ponownie',
    showMore: 'Pokaż więcej',
    showRemaining: 'Pokaż pozostałych',
    startFocusMode: 'Rozpocznij Tryb Focus',
  },
  birthday: {
    tooltip: 'Urodziny dzisiaj',
  },
  filters: {
    all: 'Cały zespół',
    label: 'Filtry i statystyki zespołu',
    options: {
      [TeamDevelopmentFilterDto.All]: 'Cały zespół',
      [TeamDevelopmentFilterDto.OverdueSteps]: 'Zaległe kroki',
      [TeamDevelopmentFilterDto.PlansToApprove]: 'Plany do zatwierdzenia',
      [TeamDevelopmentFilterDto.WithoutPlan]: 'Brak planu rozwoju',
    },
    overdueSteps: 'Zaległe kroki',
    plansToApprove: 'Plany do zatwierdzenia',
    withoutPlan: 'Brak planu rozwoju',
  },
  focus: {
    acceptanceStatus: 'Status akceptacji',
    clickToProfile: 'Kliknij, aby przejść do profilu',
    closePreview: 'Zamknij podgląd',
    emptyFilter: 'Brak pracowników w tym filtrze',
    fullProfile: 'Pełny profil pracownika',
    milestones: 'Kamienie milowe',
    noActionNeeded: 'Ten pracownik nie wymaga działania z Twojej strony w tej chwili.',
    noActivePlan: 'Brak aktywnego planu do podglądu.',
    pathLoadError: 'Nie udało się załadować szczegółów planu.',
    planDevelopment: 'Plan rozwoju',
    planSteps: '{{count}} kroków',
    progress: 'Postęp planu',
  },
  list: {
    columns: {
      employee: 'Pracownik',
      matrixField: 'Pole na matrycy',
      planProgress: 'Postęp planu',
      pmc: 'PMC',
      sop: 'SOP',
      status: 'Status planu',
    },
    count: '{{count}} pracowników',
    empty: 'Brak wyników',
    loadError: 'Nie udało się załadować listy pracowników.',
    loadingMore: 'Ładowanie...',
    loadMore: 'Załaduj więcej pracowników',
    mdrAll: 'Wszystkie kategorie MDR',
    mdrLabel: 'Kategoria MDR',
    searchPlaceholder: 'Szukaj po nazwisku, stanowisku...',
    showingCount: 'Wyświetlono {{shown}} z {{total}} pracowników',
    statusAll: 'Wszyscy',
    statusLabel: 'Status planu',
  },
  matrix: {
    definition: 'Definicja',
    developmentDirection: 'Kierunek rozwoju',
    empty: 'Brak pracowników',
    filters: {
      clear: 'Wyczyść filtry ({{count}})',
      departments: 'Dział',
      positions: 'Stanowisko',
      relation: {
        [TeamMatrixSupervisorRelationDto.N1]: 'Poziom n-1',
        [TeamMatrixSupervisorRelationDto.N2]: 'Poziom n-2',
        [TeamMatrixSupervisorRelationDto.N3]: 'Poziom n-3',
        [TeamMatrixSupervisorRelationDto.Subtree]: 'Wszyscy podlegli',
      },
      relationHint: 'Wybierz przełożonego, aby filtrować po poziomie względnym.',
      selectedCount: 'Wybrano: {{count}}',
      subordinates_few: '{{count}} osoby',
      subordinates_many: '{{count}} osób',
      subordinates_one: '{{count}} osoba',
      supervisor: 'Przełożony',
      supervisorAll: 'Wszyscy przełożeni',
    },
    loadError: 'Nie udało się załadować macierzy.',
    loadMoreError: 'Nie udało się pobrać kolejnych osób.',
    plan: 'Plan {{progress}}%',
    progressTooltip: 'Postęp planu: {{progress}}%',
    tenureYears: 'Staż pracy w JMP',
    tenureYearsValue_few: '{{count}} lata',
    tenureYearsValue_many: '{{count}} lat',
    tenureYearsValue_one: '{{count}} rok',
  },
  mdr: {
    fallback: 'Poza MDR',
    [MatrixTypeDto.Alert]: {
      description:
        'Pracownicy, których aktualne wyniki są niesatysfakcjonujące lub uległy pogorszeniu i wymagają jasno zdefiniowanego planu naprawczego.',
      label: 'Alert',
    },
    [MatrixTypeDto.EffectiveInRole]: {
      description:
        'Pracownicy, którzy niezawodnie realizują stawiane cele i rozwijają się ekspercko w swoim obszarze.',
      label: 'Skuteczny/a w Roli',
    },
    [MatrixTypeDto.EmergingLeader]: {
      description:
        'Pracownicy o wysokim potencjale liderskim i osiągający oczekiwane wyniki. Predysponują do awansu o 1 poziom.',
      label: 'Wschodzący/a Lider/ka',
    },
    [MatrixTypeDto.FutureLeader]: {
      description:
        'Pracownicy o wysokim potencjale liderskim i osiągający wysokie wyniki. Predysponują do awansu o 2 lub więcej poziomów.',
      label: 'Lider/ka Przyszłości',
    },
    [MatrixTypeDto.NewEmployee]: {
      description:
        'Nowi pracownicy w trakcie wdrożenia, których potencjał i kierunek rozwoju są jeszcze obserwowane.',
      label: 'Nowy Pracownik',
    },
    [MatrixTypeDto.QuestionMark]: {
      description:
        'Pracownicy, którzy wymagają wsparcia i czasu, by potwierdzić dopasowanie do stanowiska lub odkryć swoje możliwości.',
      label: 'Znak Zapytania',
    },
    [MatrixTypeDto.SubjectMatterLeader]: {
      description:
        'Pracownicy z potencjałem liderskim, regularnie osiągający wyniki. Predysponują do poszerzenia zakresu odpowiedzialności.',
      label: 'Lider/ka Merytoryczny/a',
    },
    OUTSIDE_MDR: {
      description:
        'Pracownicy z własnym planem rozwojowym stworzonym indywidualnie, poza firmowym procesem MDR.',
      label: 'Poza MDR',
    },
  },
  overview: {
    empty: 'Brak pracowników spełniających kryteria',
    loadError: 'Nie udało się załadować pracowników.',
  },
  review: {
    cancel: 'Anuluj',
    commentLabel: 'Komentarz dla pracownika',
    commentPlaceholder: 'Wpisz, co wymaga poprawy w tym planie...',
    requestChanges: 'Poproś o zmiany',
    submitRequest: 'Wyślij prośbę o zmiany',
  },
  status: {
    [TeamPlanStatusDto.Approved]: 'Zatwierdzony',
    [TeamPlanStatusDto.Draft]: 'Szkic',
    [TeamPlanStatusDto.None]: 'Brak planu',
    [TeamPlanStatusDto.Overdue]: 'Plan zaległy',
    [TeamPlanStatusDto.Waiting]: 'Czeka na zatwierdzenie',
  },
  tabs: {
    list: 'Lista pracowników',
    overview: 'Przegląd zespołu',
  },
  toast: {
    acceptanceError: 'Nie udało się zaktualizować statusu planu.',
    acceptanceSuccess: 'Zaktualizowano status planu.',
  },
};
